import cookies from 'js-cookie';
import {
  buildConsolidatedOutsourcedOrderTicketViewModel,
  buildOutsourcedOrderEscPosTicket,
  buildOutsourcedOrderTicketViewModel,
  type OutsourcedOrderTicketLabels,
  type OutsourcedOrderTicketInput,
  type OutsourcedOrderTicketProductInput,
  type OutsourcedOrderTicketViewModel,
  type TicketImageAsset,
  type TicketSectionInput,
} from '@washday/utilities';
import type { IOutsourcedOrder } from '../hooks/outsourcingOrders/interfaces';
import type { IStore } from '../interfaces/Store';
import { getSectionsV2 } from '../services/sections.service';
import { openEscPosFallbackTab } from './escposFallbackTab';
import { showNotification, updateNotification } from './Notification/Notification';
import Printer from './qz-print';
import {
  createEscPosQzPrintData,
  resolveEscPosPaperWidth,
  resolveEscPosTicketLogoAsset
} from './ticketEscPos';

type TicketTranslator = (key: string) => string;

interface TryPrintOutsourcedOrderTicketParams {
  outsourcedOrder: IOutsourcedOrder;
  store?: IStore | null;
  companyLogoUrl?: string;
  t: TicketTranslator;
}

interface TryPrintConsolidatedOutsourcedOrderTicketParams extends TryPrintOutsourcedOrderTicketParams {
  outsourcedOrders: IOutsourcedOrder[];
}

interface PrintOutsourcedOrderTicketViewModelParams {
  t: TicketTranslator;
  companyLogoUrl?: string;
  shouldPrintLogo?: boolean;
  buildViewModel: (logo?: TicketImageAsset) => OutsourcedOrderTicketViewModel;
}

const NO_PARTNER_GROUP_KEY = 'no-partner';
const ACTIVE_OUTSOURCED_TICKET_STATUSES = new Set(['pending', 'in_process']);
const MONGO_OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const UUID_PATTERN = /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;

type SectionLookup = Map<string, TicketSectionInput>;

function getResponseSections(response: unknown): unknown[] {
  const data = response as {
    data?: {
      data?: {
        sections?: unknown[];
      };
      sections?: unknown[];
    };
  };

  if (Array.isArray(data?.data?.data?.sections)) return data.data.data.sections;
  if (Array.isArray(data?.data?.sections)) return data.data.sections;
  return [];
}

function getSectionId(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof (value as { toHexString?: () => string }).toHexString === 'function') {
    return (value as { toHexString: () => string }).toHexString();
  }
  if (typeof value === 'object' && '_id' in value) {
    return getSectionId((value as { _id?: unknown })._id);
  }
  return '';
}

function getSectionName(value: unknown): string {
  if (!value || typeof value !== 'object' || !('name' in value)) return '';
  const name = (value as { name?: unknown }).name;
  return typeof name === 'string' ? name.trim() : '';
}

function isOpaqueSectionId(value: string): boolean {
  return MONGO_OBJECT_ID_PATTERN.test(value) || UUID_PATTERN.test(value);
}

function buildSectionLookup(sections: unknown[]): SectionLookup {
  return sections.reduce<SectionLookup>((lookup, section) => {
    const sectionId = getSectionId(section);
    const sectionName = getSectionName(section);

    if (sectionId && sectionName) {
      lookup.set(sectionId, {
        _id: sectionId,
        name: sectionName,
      });
    }

    return lookup;
  }, new Map());
}

function hydrateProductSection(
  product: OutsourcedOrderTicketProductInput,
  sectionLookup: SectionLookup,
): OutsourcedOrderTicketProductInput {
  const section = product.section;
  if (!section) return product;

  const sectionId = getSectionId(section);
  const matchedSection = sectionId ? sectionLookup.get(sectionId) : undefined;
  if (matchedSection) {
    return {
      ...product,
      section: matchedSection,
    };
  }

  if (getSectionName(section)) return product;

  if (sectionId && (typeof section !== 'string' || isOpaqueSectionId(sectionId))) {
    return {
      ...product,
      section: {
        _id: sectionId,
      },
    };
  }

  return product;
}

function hydrateOrderSections(
  order: OutsourcedOrderTicketInput['order'],
  sectionLookup: SectionLookup,
): OutsourcedOrderTicketInput['order'] {
  if (!order || typeof order === 'string') return order;

  return {
    ...order,
    products: order.products?.map((product) => hydrateProductSection(product, sectionLookup)),
    buyAndGetProducts: order.buyAndGetProducts?.map((product) =>
      hydrateProductSection(product, sectionLookup),
    ),
  };
}

function hydrateOutsourcedOrderSections(
  outsourcedOrder: OutsourcedOrderTicketInput,
  sectionLookup?: SectionLookup,
): OutsourcedOrderTicketInput {
  if (!sectionLookup) return outsourcedOrder;

  return {
    ...outsourcedOrder,
    order: hydrateOrderSections(outsourcedOrder.order, sectionLookup),
    relatedProduct: outsourcedOrder.relatedProduct
      ? hydrateProductSection(outsourcedOrder.relatedProduct, sectionLookup)
      : outsourcedOrder.relatedProduct,
  };
}

async function resolveOutsourcedTicketSectionLookup(
  store: IStore | undefined,
  ticketStructure: IStore['outsourcedOrderTicketStructure'] | undefined,
): Promise<SectionLookup | undefined> {
  if (!ticketStructure?.groupProductsBySection) return undefined;

  const storeId = resolveOutsourcedOrderEntityId(store?._id);
  if (!storeId) return new Map();

  try {
    const response = await getSectionsV2({ storeId, isActive: true });
    return buildSectionLookup(getResponseSections(response));
  } catch {
    return new Map();
  }
}

function resolveTicketStore(
  outsourcedOrder: IOutsourcedOrder,
  explicitStore?: IStore | null,
): IStore | undefined {
  if (explicitStore) return explicitStore;
  const { store } = outsourcedOrder;
  return store && typeof store !== 'string' ? store : undefined;
}

function buildTicketOutsourcedOrder(outsourcedOrder: IOutsourcedOrder): OutsourcedOrderTicketInput {
  return {
    _id: outsourcedOrder._id,
    outsourcedOrderSequence: outsourcedOrder.outsourcedOrderSequence,
    outsourcedStatus: outsourcedOrder.outsourcedStatus,
    sentDate: outsourcedOrder.sentDate,
    receivedDate: outsourcedOrder.receivedDate,
    externalOrderId: outsourcedOrder.externalOrderId,
    partner: outsourcedOrder.partner as OutsourcedOrderTicketInput['partner'],
    order: outsourcedOrder.order as OutsourcedOrderTicketInput['order'],
    orderProduct: outsourcedOrder.orderProduct,
    relatedProduct: outsourcedOrder.relatedProduct as OutsourcedOrderTicketInput['relatedProduct'],
    partnerNotes: outsourcedOrder.partnerNotes,
    internalNotes: outsourcedOrder.internalNotes,
  };
}

function preferObjectValue<T>(
  primary: T | string | null | undefined,
  fallback: T | string | null | undefined,
) {
  if (primary && typeof primary !== 'string') return primary;
  if (fallback && typeof fallback !== 'string') return fallback;
  return primary ?? fallback;
}

export function resolveOutsourcedOrderEntityId(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof (value as { toHexString?: () => string }).toHexString === 'function') {
    return (value as { toHexString: () => string }).toHexString();
  }
  if (typeof value === 'object' && value && '_id' in value) {
    return resolveOutsourcedOrderEntityId((value as { _id?: unknown })._id);
  }
  return String(value);
}

export function getOutsourcedOrderPrintGroupKey(outsourcedOrder: IOutsourcedOrder): string {
  const orderId = resolveOutsourcedOrderEntityId(outsourcedOrder.order);
  const partnerId = resolveOutsourcedOrderEntityId(outsourcedOrder.partner) || NO_PARTNER_GROUP_KEY;
  return `${orderId}::${partnerId}`;
}

function sortOutsourcedOrdersForTicketGroup(outsourcedOrders: IOutsourcedOrder[]): IOutsourcedOrder[] {
  return outsourcedOrders
    .map((outsourcedOrder, index) => ({ outsourcedOrder, index }))
    .sort((left, right) => {
      const leftSequence = left.outsourcedOrder.outsourcedOrderSequence;
      const rightSequence = right.outsourcedOrder.outsourcedOrderSequence;
      const leftHasSequence = typeof leftSequence === 'number';
      const rightHasSequence = typeof rightSequence === 'number';

      if (leftHasSequence && rightHasSequence && leftSequence !== rightSequence) {
        return leftSequence - rightSequence;
      }

      if (leftHasSequence && !rightHasSequence) return -1;
      if (!leftHasSequence && rightHasSequence) return 1;

      return left.index - right.index;
    })
    .map(({ outsourcedOrder }) => outsourcedOrder);
}

export function getOutsourcedOrdersForSamePrintGroup(
  selectedOutsourcedOrder: IOutsourcedOrder,
  outsourcedOrders: IOutsourcedOrder[],
): IOutsourcedOrder[] {
  const selectedGroupKey = getOutsourcedOrderPrintGroupKey(selectedOutsourcedOrder);
  return sortOutsourcedOrdersForTicketGroup(
    outsourcedOrders.filter((outsourcedOrder) =>
      getOutsourcedOrderPrintGroupKey(outsourcedOrder) === selectedGroupKey &&
      ACTIVE_OUTSOURCED_TICKET_STATUSES.has(outsourcedOrder.outsourcedStatus)
    ),
  );
}

function buildLabels(t: TicketTranslator): Partial<OutsourcedOrderTicketLabels> {
  return {
    title: t('OutsourcingPage:printTicket.title'),
    orderLabel: t('OutsourcingPage:printTicket.order'),
    outsourcedOrderLabel: t('OutsourcingPage:printTicket.outsourcedOrder'),
    sentDateLabel: t('OutsourcingPage:printTicket.sentDate'),
    receivedDateLabel: t('OutsourcingPage:printTicket.receivedDate'),
    statusLabel: t('OutsourcingPage:printTicket.status'),
    partnerLabel: t('OutsourcingPage:printTicket.partner'),
    partnerPhoneLabel: t('OutsourcingPage:printTicket.partnerPhone'),
    externalOrderIdLabel: t('OutsourcingPage:printTicket.externalOrderId'),
    customerLabel: t('OutsourcingPage:printTicket.customer'),
    customerPhoneLabel: t('OutsourcingPage:printTicket.customerPhone'),
    conceptsLabel: t('OutsourcingPage:printTicket.concepts'),
    productLabel: t('OutsourcingPage:printTicket.product'),
    quantityLabel: t('OutsourcingPage:printTicket.quantity'),
    piecesLabel: t('OutsourcingPage:printTicket.pieces'),
    colorLabel: t('OutsourcingPage:printTicket.color'),
    damageLabel: t('OutsourcingPage:printTicket.damage'),
    stainLabel: t('OutsourcingPage:printTicket.stain'),
    brandLabel: t('OutsourcingPage:printTicket.brand'),
    notesLabel: t('OutsourcingPage:printTicket.notes'),
    partnerNotesLabel: t('OutsourcingPage:printTicket.partnerNotes'),
    internalNotesLabel: t('OutsourcingPage:printTicket.internalNotes'),
    missingProductLabel: t('OutsourcingPage:printTicket.missingProduct'),
    noPricesLegend: t('OutsourcingPage:printTicket.noPricesLegend'),
    statuses: {
      pending: t('FieldName:outsourcedStatuses.pending'),
      in_process: t('FieldName:outsourcedStatuses.in_process'),
      returned: t('FieldName:outsourcedStatuses.returned'),
    },
  };
}

function getErrorMessage(error: unknown): string | undefined {
  return error instanceof Error ? error.message : undefined;
}

async function resolveOutsourcedTicketLogo({
  companyLogoUrl,
  paperWidth,
  shouldPrintLogo,
}: {
  companyLogoUrl?: string;
  paperWidth: 58 | 80;
  shouldPrintLogo?: boolean;
}): Promise<TicketImageAsset | undefined> {
  if (!shouldPrintLogo || !companyLogoUrl) return undefined;

  try {
    return await resolveEscPosTicketLogoAsset(companyLogoUrl, paperWidth);
  } catch {
    return undefined;
  }
}

function updateFallbackNotification({
  bytes,
  notificationId,
  t,
  message,
}: {
  bytes: Uint8Array;
  notificationId: string;
  t: TicketTranslator;
  message: string;
}): void {
  const opened = openEscPosFallbackTab({
    bytes,
    title: t('OutsourcingPage:printTicket.title'),
  });

  updateNotification({
    id: notificationId,
    loading: false,
    title: opened
      ? t('OutsourcingPage:printTicket.fallbackOpened')
      : t('OutsourcingPage:printTicket.fallbackBlocked'),
    message,
  });
}

async function printOutsourcedOrderTicketViewModel({
  t,
  companyLogoUrl,
  shouldPrintLogo,
  buildViewModel,
}: PrintOutsourcedOrderTicketViewModelParams): Promise<void> {
  const notificationId = showNotification({
    loading: true,
    title: t('OutsourcingPage:printTicket.checkingPrinter'),
    type: 'Info',
  });

  const printerName = cookies.get('printer') || localStorage.getItem('printer');

  try {
    const printerPaperSize =
      cookies.get('printerPaperSize') || localStorage.getItem('printerPaperSize');
    const paperWidth = resolveEscPosPaperWidth(printerPaperSize);
    const logo = await resolveOutsourcedTicketLogo({
      companyLogoUrl,
      paperWidth,
      shouldPrintLogo,
    });
    const viewModel = buildViewModel(logo);
    const bytes = buildOutsourcedOrderEscPosTicket(viewModel, {
      paperWidth,
      includeCut: true,
      feedLinesBeforeCut: 4,
    });

    if (!printerName) {
      updateFallbackNotification({
        bytes,
        notificationId,
        t,
        message: t('OutsourcingPage:printTicket.noPrinter'),
      });
      return;
    }

    const printer = new Printer();
    const connectResponse = await printer.verifyQZConnection(printerName);

    if (!connectResponse.ok) {
      updateFallbackNotification({
        bytes,
        notificationId,
        t,
        message: connectResponse.message || t('OutsourcingPage:printTicket.qzError'),
      });
      return;
    }

    const printData = createEscPosQzPrintData(bytes);

    await printer.print(
      printerName,
      {
        jobName: 'Washday - Outsourced Order Ticket',
      },
      printData,
    );

    updateNotification({
      id: notificationId,
      loading: false,
      title: t('OutsourcingPage:printTicket.success'),
      message: printerName,
    });
  } catch (error) {
    updateNotification({
      id: notificationId,
      loading: false,
      title: t('OutsourcingPage:printTicket.error'),
      message: getErrorMessage(error),
    });
  }
}

export async function tryPrintOutsourcedOrderTicket({
  outsourcedOrder,
  store,
  companyLogoUrl,
  t,
}: TryPrintOutsourcedOrderTicketParams): Promise<void> {
  const ticketStore = resolveTicketStore(outsourcedOrder, store);
  const ticketStructure = ticketStore?.outsourcedOrderTicketStructure || undefined;
  const sectionLookup = await resolveOutsourcedTicketSectionLookup(ticketStore, ticketStructure);
  const ticketOutsourcedOrder = hydrateOutsourcedOrderSections(
    buildTicketOutsourcedOrder(outsourcedOrder),
    sectionLookup,
  );

  await printOutsourcedOrderTicketViewModel({
    t,
    companyLogoUrl,
    shouldPrintLogo: ticketStructure?.printLogoOnTicket === true,
    buildViewModel: (logo) => buildOutsourcedOrderTicketViewModel({
      outsourcedOrder: ticketOutsourcedOrder,
      store: ticketStore,
      ticketStructure,
      labels: buildLabels(t),
      logo,
      options: {
        includeCustomerName: Boolean(ticketStructure?.showCustomerName),
        includeCustomerPhone: false,
        includeInternalNotes: false,
        includeOrderNotes: Boolean(ticketStructure?.showOrderNotes),
        includePartnerPhone: ticketStructure?.showPartnerPhone !== false,
        timeZone: ticketStore?.timezone,
      },
    }),
  });
}

export async function tryPrintConsolidatedOutsourcedOrderTicket({
  outsourcedOrder,
  outsourcedOrders,
  store,
  companyLogoUrl,
  t,
}: TryPrintConsolidatedOutsourcedOrderTicketParams): Promise<void> {
  const ticketStore = resolveTicketStore(outsourcedOrder, store);
  const ticketStructure = ticketStore?.outsourcedOrderTicketStructure || undefined;
  const sameGroupOutsourcedOrders = getOutsourcedOrdersForSamePrintGroup(
    outsourcedOrder,
    outsourcedOrders.length ? outsourcedOrders : [outsourcedOrder],
  );
  const safeGroup = sameGroupOutsourcedOrders.length ? sameGroupOutsourcedOrders : [outsourcedOrder];
  const sectionLookup = await resolveOutsourcedTicketSectionLookup(ticketStore, ticketStructure);
  const selectedTicketOutsourcedOrder = hydrateOutsourcedOrderSections(
    buildTicketOutsourcedOrder(outsourcedOrder),
    sectionLookup,
  );
  const ticketOutsourcedOrders = safeGroup.map((ticketOutsourcedOrder) =>
    hydrateOutsourcedOrderSections(buildTicketOutsourcedOrder(ticketOutsourcedOrder), sectionLookup),
  );
  const firstTicketOutsourcedOrder = ticketOutsourcedOrders[0];

  await printOutsourcedOrderTicketViewModel({
    t,
    companyLogoUrl,
    shouldPrintLogo: ticketStructure?.printLogoOnTicket === true,
    buildViewModel: (logo) => buildConsolidatedOutsourcedOrderTicketViewModel({
      consolidatedOutsourcedOrder: {
        order: preferObjectValue(
          selectedTicketOutsourcedOrder.order,
          firstTicketOutsourcedOrder?.order,
        ),
        partner: preferObjectValue(
          selectedTicketOutsourcedOrder.partner,
          firstTicketOutsourcedOrder?.partner,
        ),
        sentDate: selectedTicketOutsourcedOrder.sentDate,
        receivedDate: selectedTicketOutsourcedOrder.receivedDate,
        outsourcedOrders: ticketOutsourcedOrders,
      },
      store: ticketStore,
      ticketStructure,
      logo,
      labels: {
        ...buildLabels(t),
        title: t('OutsourcingPage:printTicket.consolidatedTitle'),
      },
      options: {
        includeCustomerName: Boolean(ticketStructure?.showCustomerName),
        includeCustomerPhone: false,
        includeInternalNotes: false,
        includeOrderNotes: Boolean(ticketStructure?.showOrderNotes),
        includePartnerPhone: ticketStructure?.showPartnerPhone !== false,
        timeZone: ticketStore?.timezone,
      },
    }),
  });
}
